import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/auth.service';
import { API_URL } from '../services/auth.service';

interface OnboardingMiddlewareProps {
  children: React.ReactNode;
}

const OnboardingMiddleware: React.FC<OnboardingMiddlewareProps> = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const checkOnboardingStatus = useCallback(async () => {
    try {
      // Primeiro verifica se está autenticado
      if (!(await authService.isAuthenticated())) {
        setLoading(false);
        return;
      }

      const token = await authService.getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      // Busca o perfil do usuário para verificar o tipo
      const userProfile = await authService.getUserProfile();

      // Verifica se é um BUSINESS_OWNER (dono de estabelecimento)
      // SYSTEM_ADMIN e FRONTDESK não devem passar por onboarding
      if (userProfile.role !== 'BUSINESS_OWNER' || userProfile.tipo_usuario === 'FRONTDESK') {
        setLoading(false);
        return;
      }

      // Verifica o status do onboarding
      const response = await fetch(`${API_URL}/estabelecimentos/onboarding-status/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json() as { onboarding_completed: boolean };

        // Se o onboarding não foi completado, redireciona
        if (!data.onboarding_completed) {
          setShouldRedirect(true);

          // Verifica se já não está na página de onboarding
          if (!window.location.pathname.includes('/onboarding')) {
            navigate('/onboarding', { replace: true });
          }
        }
      } else if (response.status === 404) {
        // Sem estabelecimento/onboarding: não bloquear o dashboard
        setShouldRedirect(false);
        setLoading(false);
        return;
      } else {
        // Em outros erros, apenas libera o fluxo para evitar loop
        console.warn('Erro ao verificar status do onboarding, liberando fluxo para dashboard');
        setShouldRedirect(false);
      }
    } catch (error) {
      console.error('Erro ao verificar status do onboarding:', error);

      // Em caso de erro, não redireciona para evitar loops
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  // Se está carregando ou redirecionando, retorna null para manter o loader do Suspense visível
  if (loading || shouldRedirect) {
    return null;
  }

  // Se tudo estiver ok, renderiza os filhos normalmente
  return <>{children}</>;
};

export default OnboardingMiddleware;
